import { ApplicantStatus } from './statusConstants';

/** Types */
type ApplicantInfo = ApiSchema<'ResumeCardData'>;

/**
 * @description Filters out applicants based on their statusId. If the statusId is present in the provided list,
 * the applicant will be excluded from the result.
 * - `applicants`: *ApplicantInfo[]* - The list of applicants to filter.
 * - `statusIdsToFilterBy`: *ApplicantStatus[]* - The status IDs to filter by.
 * - `keepApplicantsWithMissingStatusId`: *boolean* - If true, applicants with a missing status ID will be included in the result.
 *
 * @returns *ApplicantInfo[]* - The filtered list of applicants.
 * @example
 * const filteredApplicants = filterOutApplicantsByStatus(applicants, [1, 3]);
 */
export const filterByStatus = (
  applicants?: ApplicantInfo[],
  statusIdsToFilterBy?: ApplicantStatus[],
  keepApplicantsWithMissingStatusId = false,
) => {
  if (!applicants || !statusIdsToFilterBy || statusIdsToFilterBy.length === 0 || !Array.isArray(applicants)) {
    return applicants;
  }

  const statusIdSet = new Set(statusIdsToFilterBy);

  // Filter applicants: include an applicant if their application statusId is present in the statusIdSet.
  return applicants.filter((applicant) => {
    const applicantStatusId = applicant?.application?.statusId;
    // If keepApplicantsWithMissingStatusId is true, include applicants with a missing statusId.
    if (keepApplicantsWithMissingStatusId && !applicantStatusId) return true;
    // Ensure applicantStatusId is a number before checking its presence in the Set.
    return typeof applicantStatusId === 'number' && !statusIdSet.has(applicantStatusId);
  });
};
